import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import { Tracker } from '../../analytics/services/Tracker';
import { MEDIA } from '../../analytics/constants/MetricEvents';
import { processImageForUpload } from '../utils/imageProcessor';
import { MediaUploadClientError } from '../models/MediaUploadClientError';
import {
  getMediaUploadFriendlyMessage,
  type MediaUploadTranslateFn,
} from '../utils/mediaUploadFriendlyMessage';
import { createDeadlineSignal, DEFAULT_MEDIA_UPLOAD_FETCH_MS } from '../utils/mediaUploadFetch';
import { normalizeMediaUploadFailureReason, type UploadFailureStage } from '../utils/mediaUploadAnalytics';
import type { MediaKind } from '../models/MediaUploadModels';

type UploadUrlResponse = {
  uploadUrl: string;
  objectKey: string;
  expiresIn: number;
  uploadHeaders?: Record<string, string>;
};

export { MediaUploadClientError } from '../models/MediaUploadClientError';

type PreparedUploadPayload = {
  blob: Blob;
  mimeType: string;
  contentLength: number;
};

const STORAGE_PUT_MAX_ATTEMPTS = 3;
const STORAGE_PUT_RETRY_BASE_MS = 300;
const STORAGE_PUT_RETRY_MAX_MS = 2000;

async function readApiErrorMessage(response: Response): Promise<string | null> {
  try {
    const payload = await response.json() as { error?: string };
    return payload?.error || null;
  } catch {
    return null;
  }
}

async function fetchWithUploadDeadline(
  run: (signal: AbortSignal) => Promise<Response>,
): Promise<Response> {
  const { signal, clearDeadline } = createDeadlineSignal(DEFAULT_MEDIA_UPLOAD_FETCH_MS);
  try {
    return await run(signal);
  } finally {
    clearDeadline();
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getStoragePutRetryDelayMs(attempt: number): number {
  const safeAttempt = Math.max(1, attempt);
  const baseDelay = Math.min(STORAGE_PUT_RETRY_MAX_MS, STORAGE_PUT_RETRY_BASE_MS * (2 ** (safeAttempt - 1)));
  const jitter = Math.floor(Math.random() * Math.max(50, Math.floor(baseDelay * 0.2)));
  return Math.min(STORAGE_PUT_RETRY_MAX_MS, baseDelay + jitter);
}

function isRetryableStoragePutError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError') return true;
  if (error instanceof TypeError) return true;
  return false;
}

function isRetryableStoragePutStatus(status: number): boolean {
  return status >= 500 && status < 600;
}

async function putToStorageWithRetry(input: {
  uploadUrl: string;
  uploadHeaders: Record<string, string> | undefined;
  mimeType: string;
  blob: Blob;
}): Promise<void> {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= STORAGE_PUT_MAX_ATTEMPTS; attempt += 1) {
    try {
      const putResponse = await fetchWithUploadDeadline((signal) =>
        fetch(input.uploadUrl, {
          method: 'PUT',
          headers: input.uploadHeaders ?? { 'Content-Type': input.mimeType },
          body: input.blob,
          signal,
        }),
      );

      if (putResponse.ok) {
        return;
      }

      if (!isRetryableStoragePutStatus(putResponse.status) || attempt === STORAGE_PUT_MAX_ATTEMPTS) {
        throw new MediaUploadClientError('File upload failed while sending data to storage.');
      }

      await sleep(getStoragePutRetryDelayMs(attempt));
      continue;
    } catch (error) {
      lastError = error;
      if (!isRetryableStoragePutError(error) || attempt === STORAGE_PUT_MAX_ATTEMPTS) {
        throw new MediaUploadClientError('File upload failed while sending data to storage.');
      }
      await sleep(getStoragePutRetryDelayMs(attempt));
    }
  }
  throw new MediaUploadClientError(
    lastError instanceof Error
      ? lastError.message
      : 'File upload failed while sending data to storage.',
  );
}

function isLikelyPdfByFilename(fileName: string): boolean {
  return fileName.trim().toLowerCase().endsWith('.pdf');
}

async function prepareUploadPayload(file: File, mediaKind: MediaKind): Promise<PreparedUploadPayload> {
  if (mediaKind === 'image') {
    const processed = await processImageForUpload(file);
    return {
      blob: processed.blob,
      mimeType: processed.mimeType,
      contentLength: processed.contentLength,
    };
  }

  const rawMimeType = (file.type || '').trim().toLowerCase();
  const resolvedMimeType =
    rawMimeType === 'application/pdf'
      ? 'application/pdf'
      : rawMimeType.length === 0 && isLikelyPdfByFilename(file.name)
        ? 'application/pdf'
        : null;

  if (!resolvedMimeType) {
    throw new MediaUploadClientError('Only PDF files are supported.');
  }

  return {
    blob: file,
    mimeType: resolvedMimeType,
    contentLength: file.size,
  };
}

export function useMediaUpload(options?: { source?: string; diagramId?: string; mediaKind?: MediaKind }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { t } = useTranslation('diagram');
  const source = options?.source ?? 'orgchart.property_panel';
  const diagramId = options?.diagramId?.trim();
  const mediaKind = options?.mediaKind ?? 'image';

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      let stage: UploadFailureStage = mediaKind === 'image' ? 'image_processing' : 'upload_url';
      try {
        const prepared = await prepareUploadPayload(file, mediaKind);
        Tracker.track(MEDIA.UPLOAD_STARTED, {
          source,
          mediaKind,
          mimeType: prepared.mimeType,
          contentLength: prepared.contentLength,
        });
        Tracker.track(MEDIA.UPLOAD_URL_REQUESTED, {
          source,
          mediaKind,
          mimeType: prepared.mimeType,
          contentLength: prepared.contentLength,
        });

        stage = 'upload_url';
        const uploadUrlResponse = await fetchWithUploadDeadline((signal) =>
          authenticatedFetch('/api/media/upload-url', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              mediaKind,
              mimeType: prepared.mimeType,
              contentLength: prepared.contentLength,
              ...(diagramId ? { diagramId } : {}),
            }),
            signal,
          }),
        );

        if (!uploadUrlResponse.ok) {
          const apiError = await readApiErrorMessage(uploadUrlResponse);
          throw new MediaUploadClientError(apiError || 'Could not get an upload URL.');
        }

        const payload = (await uploadUrlResponse.json()) as UploadUrlResponse;
        if (!payload.uploadUrl || !payload.objectKey) {
          throw new MediaUploadClientError('Upload URL response was invalid.');
        }

        stage = 'storage_put';
        await putToStorageWithRetry({
          uploadUrl: payload.uploadUrl,
          uploadHeaders: payload.uploadHeaders,
          mimeType: prepared.mimeType,
          blob: prepared.blob,
        });

        stage = 'verify';
        const verifyResponse = await fetchWithUploadDeadline((signal) =>
          authenticatedFetch('/api/media/verify-object', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              mediaKind,
              objectKey: payload.objectKey,
              ...(diagramId ? { diagramId } : {}),
            }),
            signal,
          }),
        );

        if (!verifyResponse.ok) {
          const apiError = await readApiErrorMessage(verifyResponse);
          Tracker.track(MEDIA.VERIFY_FAILED, {
            source,
            mediaKind,
            status: verifyResponse.status,
            error: apiError || 'verify_failed',
            failureReason: 'verify_failed',
          });
          throw new MediaUploadClientError(apiError || 'Uploaded file failed validation.');
        }

        Tracker.track(MEDIA.UPLOAD_COMPLETED, {
          source,
          mediaKind,
          mimeType: prepared.mimeType,
          contentLength: prepared.contentLength,
        });

        return {
          objectKey: payload.objectKey,
          previewBlob: prepared.blob,
        };
      } catch (error) {
        Tracker.track(MEDIA.UPLOAD_FAILED, {
          source,
          mediaKind,
          stage,
          failureReason: normalizeMediaUploadFailureReason(error, stage),
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });

  return {
    uploadMedia: mutation.mutateAsync,
    uploadImage: mutation.mutateAsync,
    isUploading: mutation.isPending,
    reset: mutation.reset,
    errorMessage: mutation.error
      ? getMediaUploadFriendlyMessage(mutation.error, t as MediaUploadTranslateFn)
      : null,
  };
}
